import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminAPI, AiMetric, Evaluator } from "@/app/apis/admin";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { MASTER_FINDINGS } from "./Overview";

export default function AdminFindings() {
  const [metrics, setMetrics] = useState<AiMetric[]>([]);
  const [label, setLabel] = useState("");
  const [studyId, setStudyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [evaluatorsLoading, setEvaluatorsLoading] = useState(false);
  const [selectedEvaluator, setSelectedEvaluator] = useState("");
  const [activeStudyId, setActiveStudyId] = useState<string | null>(null);

  useEffect(() => {
    fetchAllMetrics();
    fetchEvaluators();
  }, []);

  const fetchEvaluators = async () => {
    try {
      setEvaluatorsLoading(true);
      const data = await AdminAPI.getEvaluators();
      setEvaluators(data || []);
    } catch (err) {
      console.error("Failed to load evaluators:", err);
      setEvaluators([]);
    } finally {
      setEvaluatorsLoading(false);
    }
  };

  const fetchAllMetrics = async (username?: string) => {
    try {
      setLoading(true);
      setActiveStudyId(null);
      const data = await AdminAPI.getMetrics(undefined, username);
      setMetrics(data || []);
    } catch (err) {
      console.error("Failed to load metrics:", err);
      toast.error("Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async (targetStudyId: string, username?: string) => {
    try {
      setLoading(true);
      setActiveStudyId(targetStudyId);
      const data = await AdminAPI.getStudyMetrics(targetStudyId, username);
      setMetrics(data || []);
    } catch (err) {
      console.error("Failed to load metrics:", err);
      toast.error("Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  const runEvaluation = async () => {
    if (!studyId.trim()) return;
    try {
      setEvaluating(true);
      await AdminAPI.runEvaluation(studyId);
      await fetchMetrics(studyId, selectedEvaluator || undefined);
      setLabel("");

      toast.success(`✓ Evaluation completed for Study ${studyId}`);

      setStudyId("");
    } catch (err) {
      console.error("Evaluation failed:", err);
      toast.error("Evaluation failed - see console for details");
    } finally {
      setEvaluating(false);
    }
  };

  const current = useMemo(() => {
    if (!metrics.length) return null;
    if (!label) {
      const totals = metrics.reduce(
        (acc, metric) => ({
          tp: acc.tp + metric.tp,
          fp: acc.fp + metric.fp,
          tn: acc.tn + metric.tn,
          fn: acc.fn + metric.fn,
        }),
        { tp: 0, fp: 0, tn: 0, fn: 0 }
      );
      const totalSamples = totals.tp + totals.fp + totals.tn + totals.fn;
      const avgSensitivity = totalSamples > 0 ? totals.tp / (totals.tp + totals.fn) : 0;
      const avgSpecificity = totalSamples > 0 ? totals.tn / (totals.tn + totals.fp) : 0;
      const avgAccuracy = totalSamples > 0 ? (totals.tp + totals.tn) / totalSamples : 0;
      return {
        label: "ALL FINDINGS",
        ...totals,
        sensitivity: avgSensitivity,
        specificity: avgSpecificity,
        accuracy: avgAccuracy,
      };
    }
    return metrics.find((m) => m.label === label) ?? null;
  }, [metrics, label]);

  const total =
    (current?.tp ?? 0) +
    (current?.tn ?? 0) +
    (current?.fp ?? 0) +
    (current?.fn ?? 0);

  const chartData = useMemo(() => {
    if (!current) return [];
    return [
      { name: "Sensitivity", value: current.sensitivity * 100 },
      { name: "Specificity", value: current.specificity * 100 },
      { name: "Accuracy", value: current.accuracy * 100 },
    ];
  }, [current]);

  const displayedFindings = useMemo(() => {
    if (!label) return MASTER_FINDINGS;
    return MASTER_FINDINGS.filter((d) => d.key === label);
  }, [label]);

  const handleEvaluatorChange = (value: string) => {
    setSelectedEvaluator(value);
    if (activeStudyId) {
      fetchMetrics(activeStudyId, value || undefined);
    } else {
      fetchAllMetrics(value || undefined);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Evaluation Metrics</h1>
          <p className="text-muted-foreground">
            Confusion matrix and detailed findings analysis
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Input
            placeholder="Enter Study ID"
            value={studyId}
            onChange={(e) => setStudyId(e.target.value)}
            className="w-48"
            disabled={evaluating}
          />
          <Button
            onClick={runEvaluation}
            disabled={evaluating || !studyId.trim()}
            className="gap-2"
          >
            {evaluating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              "Run Evaluation"
            )}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="evaluator-filter">
            Evaluator
          </label>
          <select
            id="evaluator-filter"
            aria-label="Evaluator filter"
            value={selectedEvaluator}
            onChange={(e) => handleEvaluatorChange(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            disabled={evaluatorsLoading}
          >
            <option value="">All Users</option>
            {evaluators.length === 0 && (
              <option value="" disabled>
                No evaluators available
              </option>
            )}
            {evaluators.map((evaluator) => (
              <option key={evaluator.id} value={evaluator.username}>
                {evaluator.username}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="finding-filter">
            Finding
          </label>
          <select
            id="finding-filter"
            aria-label="Finding filter"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            disabled={loading}
          >
            <option value="">All Findings</option>
            {MASTER_FINDINGS.map((finding) => (
              <option key={finding.key} value={finding.key}>
                {finding.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Loading findings data...</p>
        </div>
      )}

      {!loading && metrics.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="mb-2">📊 No findings data available yet</p>
            <p className="text-sm">
              Enter a Study ID and click "Run Evaluation" to start processing
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && metrics.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>
                Confusion Matrix — {current?.label?.toUpperCase() ?? "—"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!current ? (
                <div className="text-center text-muted-foreground py-8">
                  No finding data available.
                  <br />
                  Run evaluation on studies to see results.
                </div>
              ) : (
                <>
                  <div className="flex justify-center overflow-x-auto mb-8">
                    <table className="border-collapse">
                      <thead>
                        <tr>
                          <th />
                          <th className="px-6 py-3 text-sm font-medium">
                            Radiologist Positive
                          </th>
                          <th className="px-6 py-3 text-sm font-medium">
                            Radiologist Negative
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th className="px-6 py-3 text-sm font-medium text-left">
                            AI Predicted Positive
                          </th>
                          <td className="px-6 py-4 text-center font-semibold text-green-700 border border-gray-200">
                            <div>✓ True Positive</div>
                            <div className="text-2xl">{isNaN(current.tp) ? 0 : current.tp}</div>
                          </td>
                          <td className="px-6 py-4 text-center font-semibold text-red-700 border border-gray-200">
                            <div>✗ False Positive</div>
                            <div className="text-2xl">{isNaN(current.fp) ? 0 : current.fp}</div>
                          </td>
                        </tr>
                        <tr>
                          <th className="px-6 py-3 text-sm font-medium text-left">
                            AI Predicted Negative
                          </th>
                          <td className="px-6 py-4 text-center font-semibold text-red-700 border border-gray-200">
                            <div>✗ False Negative</div>
                            <div className="text-2xl">{isNaN(current.fn) ? 0 : current.fn}</div>
                          </td>
                          <td className="px-6 py-4 text-center font-semibold text-green-700 border border-gray-200">
                            <div>✓ True Negative</div>
                            <div className="text-2xl">{isNaN(current.tn) ? 0 : current.tn}</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-muted-foreground">
                        Sensitivity
                      </div>
                      <div className="text-3xl font-bold text-blue-700">
                        {isNaN(current.sensitivity) ? "0" : (current.sensitivity * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-sm text-muted-foreground">
                        Specificity
                      </div>
                      <div className="text-3xl font-bold text-purple-700">
                        {isNaN(current.specificity) ? "0" : (current.specificity * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-muted-foreground">
                        Accuracy
                      </div>
                      <div className="text-3xl font-bold text-green-700">
                        {isNaN(current.accuracy) ? "0" : (current.accuracy * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-muted-foreground">
                        Total Samples
                      </div>
                      <div className="text-3xl font-bold text-gray-700">
                        {total}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics Chart</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(value: number | undefined) =>
                      value !== undefined && !isNaN(value) ? `${value.toFixed(1)}%` : "0%"
                    }
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          index === 0
                            ? "#3b82f6" // Sensitivity
                            : index === 1
                              ? "#10b981" // Specificity
                              : "#f59e0b" // Accuracy
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Findings Performance Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left font-medium">Finding</th>
                      <th className="p-3 text-center font-medium">TP</th>
                      <th className="p-3 text-center font-medium">FP</th>
                      <th className="p-3 text-center font-medium">TN</th>
                      <th className="p-3 text-center font-medium">FN</th>
                      <th className="p-3 text-center font-medium">
                        Sensitivity
                      </th>
                      <th className="p-3 text-center font-medium">
                        Specificity
                      </th>
                      <th className="p-3 text-center font-medium">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedFindings.map((d) => {
                        const m = metrics.find((x) => x.label === d.key);
                        const tp = m?.tp ?? 0;
                        const fp = m?.fp ?? 0;
                        const tn = m?.tn ?? 0;
                        const fn = m?.fn ?? 0;
                        const sensitivity = m?.sensitivity ?? 0;
                        const specificity = m?.specificity ?? 0;
                        const accuracy = m?.accuracy ?? 0;

                        return (
                          <tr
                            key={d.key}
                            className="border-t hover:bg-muted/50 cursor-pointer"
                            onClick={() => setLabel(d.key)}
                          >
                            <td className="p-3 font-medium">
                              {d.name}
                            </td>
                            <td className="p-3 text-center text-green-700 font-medium">{isNaN(tp) ? 0 : tp}</td>
                            <td className="p-3 text-center text-red-700 font-medium">{isNaN(fp) ? 0 : fp}</td>
                            <td className="p-3 text-center text-green-700 font-medium">{isNaN(tn) ? 0 : tn}</td>
                            <td className="p-3 text-center text-red-700 font-medium">{isNaN(fn) ? 0 : fn}</td>
                            <td className="p-3 text-center">{isNaN(sensitivity) ? "0" : (sensitivity * 100).toFixed(1)}%</td>
                            <td className="p-3 text-center">{isNaN(specificity) ? "0" : (specificity * 100).toFixed(1)}%</td>
                            <td className="p-3 text-center">{isNaN(accuracy) ? "0" : (accuracy * 100).toFixed(1)}%</td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
